// ----------------- Imports -----------------
import { useRef, useMemo } from "react"
import { useRouter } from "next/router"
import { useQuery } from "@tanstack/react-query"
import { SoftwareAppJsonLd, OrganizationJsonLd } from "next-seo"

// Components
import { MainLayout } from "components/Layout"
import SectionV2 from "components/Sections/SectionV2"
import EventOrgSkeleton from "components/Skeleton/BrandingLayoutSkeleton"
import CustomImage from "components/atoms/Image/CustomImage"

// Hooks
import { useLang } from "hooks/useLang"

// API
import fetchEventOrg from "api/event/fetchEventOrg"

// Types

// Constants
import { COMPANY_INFO } from "constants/company_info"
import {
    GOOGLE_PLAY_STORE_URL,
    APPLE_APP_STORE_URL,
} from "utils/helpers/constants"
import SocialMediaCard from "components/EventDetailsPage/SocialMediaCard"

// ----------------- Types -----------------

export interface IPromo {
    eventTitle: string
    status: boolean
    id: string
}

// ----------------- Component -----------------
const EventOrg = () => {
    const { t, multiLangVars } = useLang()
    const router = useRouter()

    // Styles
    const blurStyle = useMemo(
        () => ({
            filter: `blur(100px)`,
            opacity: 1,
            transform: "scale(1)",
            willChange: "filter",
        }),
        []
    )

    // Data fetching
    const orgID = (router?.query?.eventid as string) || "6322ed85473c2df9b81f9437"
    const { data, isLoading } = useQuery({
        queryKey: ["fetchOrg", orgID],
        queryFn: () => fetchEventOrg(orgID),
        enabled: !!orgID,
    })

    const org = data?.payload
    const events = useMemo(() => {
        return [...(org?.current || []), ...(org?.past || [])]
    }, [org])

    // Hero section ref
    const heroRef = useRef<HTMLDivElement | null>(null)

    // SEO values
    const orgName = org?.data?.[multiLangVars("name") as "ar_name" | "en_name"] || ""

    const orgDescription =
        org?.data?.[
            multiLangVars("description") as "ar_description" | "en_description"
        ] || ""

    const baseUrl = (
        process.env.NEXT_PUBLIC_SITE_URL || "https://pass-tickets.com"
    ).replace(/\/$/, "")
    const currentPath =
        (router.asPath || "/").split("#")[0].split("?")[0] || "/"
    // const canonicalUrl = `${baseUrl}${currentPath}`

    // Slider state
    // const [loaded, setLoaded] = useState(false)

    // Loading state
    if (isLoading) {
        return (
            <MainLayout>
                <EventOrgSkeleton />
            </MainLayout>
        )
    }

    // console.log({ org })

    const getEventDesktopPoster = () => {
        const firstEvent = events?.[0]
        const posterPath =
            firstEvent?.media?.posters?.web_poster?.original ||
            firstEvent?.posters?.[0] ||
            ""
        const fallback = "/svg/v2/pass_ticket_fallback_image_light.png"
        return posterPath
            ? `${process.env.NEXT_PUBLIC_Event_Img_URL}${posterPath}`
            : fallback
    }
    return (
        <>
            {/* ----------------- Structured Data ----------------- */}
            {(() => {
                const social = COMPANY_INFO?.social_media || {}
                const sameAs = Object.values(social).filter(
                    (v) =>
                        typeof v === "string" &&
                        /^https?:\/\//.test(v as string)
                ) as string[]

                return (
                    <OrganizationJsonLd
                        useAppDir={false}
                        type="Organization"
                        name="PassTickets"
                        url={baseUrl}
                        logo={`${baseUrl}/iconx/android-chrome-512x512.png`}
                        sameAs={sameAs}
                        address={{ addressCountry: "SA" }}
                        areaServed={["SA"]}
                    />
                )
            })()}

            {(() => {
                const isEn =
                    /^\/en-US(\/?|$)/.test(currentPath) ||
                    router.locale === "en-US"
                const appName = isEn ? "PassTickets" : "باس تيكيتس"

                return (
                    <>
                        <SoftwareAppJsonLd
                            useAppDir={false}
                            type="MobileApplication"
                            name={appName}
                            price="0"
                            priceCurrency="USD"
                            applicationCategory="EntertainmentApplication"
                            operatingSystem="Android"
                            offers={[
                                {
                                    "@type": "Offer",
                                    price: "0",
                                    priceCurrency: "USD",
                                    url: GOOGLE_PLAY_STORE_URL,
                                },
                            ]}
                        />
                        <SoftwareAppJsonLd
                            useAppDir={false}
                            type="MobileApplication"
                            name={appName}
                            price="0"
                            priceCurrency="USD"
                            applicationCategory="EntertainmentApplication"
                            operatingSystem="iOS"
                            offers={[
                                {
                                    "@type": "Offer",
                                    price: "0",
                                    priceCurrency: "USD",
                                    url: APPLE_APP_STORE_URL,
                                },
                            ]}
                        />
                    </>
                )
            })()}

            {/* ----------------- Page Content ----------------- */}
            <MainLayout>
                <div>
                    {/* Blur image background */}
                    <div
                        style={blurStyle}
                        ref={heroRef}
                        className="fixed left-0 top-0 -z-10 md:aspect-video h-full md:max-h-screen w-full overflow-hidden md:block"
                    >
                        <div className="h-full bg-red-50 lg-60 sm:h-full  max-h-screen w-full overflow-hidden md:block">
                            <CustomImage
                                src={getEventDesktopPoster()}
                                alt={orgName}
                                fill
                                className="object-cover transition-opacity duration-1000 opacity-100 z-10 absolute top-0 left-0 w-full h-full"
                            />
                        </div>
                    </div>

                    {/* Hero section */}
                    <div className="layout lg:max-w-[1230px]">
                        <div className="w-full lg:mt-22 mt-20 relative">
                            <div className="lg:aspect-[16/8] aspect-[11/16] rounded-[15px] w-full  backdrop-blur-md shadow-md">
                                <CustomImage
                                    src={getEventDesktopPoster()}
                                    alt={"Single-Ad"}
                                    fill
                                    className="overflow-hidden object-cover rounded-[15px]"
                                />
                                <div className="overflow-hidden rounded-[15px] bg-[#00000041] backdrop-blur-[2px] absolute w-full h-full" />

                                <div className="absolute w-full h-full flex justify-center md:justify-start">
                                    <div className="flex flex-col w-[50%]  p-5  items-center md:items-start md:p-20">
                                        {/* <div className="flex flex-col w-[50%] gap-2 backdrop-blur-[2px] p-20"> */}
                                        <div className="w-[125px] h-[125px] bg-[#38298689] backdrop-blur-sm rounded-full">
                                            <CustomImage
                                                src={
                                                    process.env
                                                        .NEXT_PUBLIC_Event_Img_URL +
                                                    (org?.data?.logo || "")
                                                }
                                                alt={"Single-Ad"}
                                                fill
                                                className="rounded-full"
                                                style={{
                                                    objectFit: "contain",
                                                    overflow: "hidden",
                                                }}
                                            />
                                        </div>

                                        <div className="flex flex-col items-center md:items-start gap-4">
                                            <span className="text-[50px] text-white font-bold  text-center md:text-left capitalize">
                                                {orgName}
                                            </span>
                                            <span className="text-white text-[20px]">
                                                {orgDescription}
                                            </span>

                                            <div className="flex flex-row gap-6 md:gap-4 md:max-w-[100px] md:flex-wrap justify-start mt-2">
                                                <SocialMediaCard event={org?.data} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Events Section */}
                    <SectionV2
                        id="org"
                        events={events}
                        title={t("events")}
                        maxEventsInARow={4}
                        layout="grid"
                        aspectRatio="widescreen"
                    />
                </div>
            </MainLayout>
        </>
    )
}

export default EventOrg
